import {
  BBox,
  Vec2,
  closestPointOnEntity,
  entityBBox,
  entityPaths,
  intersectSegments,
  type Entity,
  type EntityId,
} from '@lakaran/geometry';
import type { CadDocument } from './document';

/** Topmost entity within the pick aperture (world units), or null. */
export function hitTest(
  doc: CadDocument,
  worldPoint: Vec2,
  tolerance: number,
): EntityId | null {
  const candidates = doc.queryVisible(BBox.around(worldPoint, tolerance));

  let bestId: EntityId | null = null;
  let bestDistance = Infinity;
  let bestOrder = -Infinity;

  for (const entity of candidates) {
    const closest = closestPointOnEntity(entity, worldPoint);
    if (!closest || closest.distance > tolerance) continue;
    const order = doc.drawOrderOf(entity.id);
    if (closest.distance < bestDistance - 1e-9 || (Math.abs(closest.distance - bestDistance) <= 1e-9 && order > bestOrder)) {
      bestDistance = closest.distance;
      bestOrder = order;
      bestId = entity.id;
    }
  }
  return bestId;
}

export function hitTestAll(
  doc: CadDocument,
  worldPoint: Vec2,
  tolerance: number,
): EntityId[] {
  const candidates = doc.queryVisible(BBox.around(worldPoint, tolerance));
  const hits: Array<{ id: EntityId; distance: number }> = [];
  for (const entity of candidates) {
    const closest = closestPointOnEntity(entity, worldPoint);
    if (!closest || closest.distance > tolerance) continue;
    hits.push({ id: entity.id, distance: closest.distance });
  }
  hits.sort((a, b) => a.distance - b.distance);
  return hits.map((h) => h.id);
}

/** Entities fully inside the box (window query). */
export function entitiesInWindow(doc: CadDocument, box: BBox): EntityId[] {
  return doc
    .queryVisible(box)
    .filter((entity) => box.containsBox(entityBBox(entity)))
    .map((entity) => entity.id);
}

/** Entities inside or crossing the box. */
export function entitiesCrossing(doc: CadDocument, box: BBox): EntityId[] {
  const tolerance = Math.max(box.width, box.height) / 2000;
  const out: EntityId[] = [];
  for (const entity of doc.queryVisible(box)) {
    if (box.containsBox(entityBBox(entity))) {
      out.push(entity.id);
      continue;
    }
    if (entityTouchesBox(entity, box, tolerance)) out.push(entity.id);
  }
  return out;
}

function entityTouchesBox(entity: Entity, box: BBox, chordTolerance: number): boolean {
  const corners = box.corners();
  for (const path of entityPaths(entity, { chordTolerance })) {
    const points = path.closed ? [...path.points, path.points[0]!] : path.points;
    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1]!;
      const b = points[i]!;
      if (box.containsPoint(a) || box.containsPoint(b)) return true;
      for (let e = 0; e < 4; e++) {
        if (intersectSegments(a, b, corners[e]!, corners[(e + 1) % 4]!)) return true;
      }
    }
  }
  return false;
}
